import React from "react";
import toast from "react-hot-toast";

/**
 * Show a toast notification. Matches the previous showToast(message, type, duration) API.
 * @param {string} message - Message to show
 * @param {'success'|'error'|'info'|'warning'} [type='info'] - Toast type
 * @param {number} [duration] - Duration in ms (react-hot-toast default 4000)
 * @param {{ anchor?: HTMLElement|React.RefObject, anchorPosition?: 'above'|'below' }} [options] - Optional. If anchor is set, toast is rendered near that element. anchorPosition 'above' (default 'below') places it above the element.
 *   Example: showToast('Saved!', 'success', 3000, { anchor: buttonRef, anchorPosition: 'above' });
 */
export function showToast(message, type = "info", duration = 3000, options = {}) {
  const durationOpts = typeof duration === "number" && duration > 0 ? { duration } : {};
  const anchorEl = options?.anchor?.current ?? options?.anchor;
  const anchorPosition = options?.anchorPosition ?? "below";

  if (anchorEl && typeof anchorEl.getBoundingClientRect === "function") {
    const rect = anchorEl.getBoundingClientRect();
    const gutter = 8;
    const screenPad = 12; // minimum gap from either edge of the viewport
    const maxW = Math.min(360, window.innerWidth - screenPad * 2);
    // Center on anchor; clamp so it never exceeds the viewport edges
    const rawLeft = rect.left + rect.width / 2 - maxW / 2;
    const clampedLeft = Math.max(screenPad, Math.min(rawLeft, window.innerWidth - maxW - screenPad));
    const isAbove = anchorPosition === "above";
    const top = isAbove ? rect.top - gutter : rect.bottom + gutter;
    const style = {
      position: "fixed",
      top: `${top}px`,
      left: `${clampedLeft}px`,
      transform: isAbove ? "translateY(-100%)" : "none",
      zIndex: 2147483647,
      padding: "10px 16px",
      borderRadius: "8px",
      fontSize: "14px",
      lineHeight: 1.5,
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      width: `${maxW}px`,
      whiteSpace: "normal",
      wordBreak: "break-word",
    };
    if (type === "success") Object.assign(style, { background: "#10b981", color: "#fff" });
    else if (type === "error") Object.assign(style, { background: "#ef4444", color: "#fff" });
    else if (type === "warning") Object.assign(style, { background: "#f59e0b", color: "#fff" });
    else Object.assign(style, { background: "#374151", color: "#fff" });

    return toast.custom(
      (t) =>
        t.visible
          ? React.createElement(
              "div",
              {
                role: "status",
                "aria-live": "polite",
                style,
              },
              message
            )
          : null,
      { duration: typeof duration === "number" && duration > 0 ? duration : 3000 }
    );
  }

  if (type === "success") return toast.success(message, durationOpts);
  if (type === "error") return toast.error(message, durationOpts);
  if (type === "warning") return toast(message, { ...durationOpts, icon: "⚠️" });
  return toast(message, durationOpts);
}
